import { buildRootCommentUrl } from './comments';
import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { PagedData, PaginationMeta } from './common';

export interface FindingComment {
  id: string;
  targetType: 'FINDING';
  targetId: string;
  createdByMemberId: string | null;
  createdByAgentId: string | null;
  createdByType: string | null;
  /** 경유 축. 도구 축을 특정하지 못해도 웹 작성물과 에이전트 표면 작성물을 구분한다. */
  createdByCredentialKind: string | null;
  createdByName: string | null;
  /** 작성에 사용된 도구(에이전트 설정) 이름. 행위 주체 축과 직교한다. */
  createdByAgentName: string | null;
  updatedByMemberId?: string | null;
  updatedByAgentId?: string | null;
  updatedByCredentialKind?: string | null;
  updatedByName?: string | null;
  updatedByAgentName?: string | null;
  content: string;
  replyCount: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

type FindingCommentListResponse = {
  data: FindingComment[];
  meta: PaginationMeta;
};

type FindingCommentResponse = {
  data: FindingComment;
};

type FindingCommentQueryParams = {
  order?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
};

const buildUrl = (projectId: string, findingId: string, params: FindingCommentQueryParams = {}) => {
  const query = new URLSearchParams();
  if (params.order) query.set('order', params.order);
  if (params.page) query.set('page', String(params.page));
  if (params.pageSize) query.set('pageSize', String(params.pageSize));
  const queryString = query.toString();
  return `${getApiUrl()}/api/projects/${projectId}/code-reviews/findings/${findingId}/comments${queryString ? `?${queryString}` : ''}`;
};

export const fetchFindingComments = async (
  token: string,
  projectId: string,
  findingId: string,
  params: FindingCommentQueryParams = {},
): Promise<PagedData<FindingComment>> => {
  const response = await fetchWithAuthRetry(buildUrl(projectId, findingId, params), {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch finding comments: ${response.status}`);
  }

  return (await response.json()) as FindingCommentListResponse;
};

export const createFindingComment = async (
  token: string,
  projectId: string,
  findingId: string,
  input: { content: string },
): Promise<FindingComment> => {
  const response = await fetchWithAuthRetry(buildUrl(projectId, findingId), {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create finding comment: ${response.status}`);
  }

  return ((await response.json()) as FindingCommentResponse).data;
};

// 수정/삭제는 targetType과 무관한 공통 원댓글 표면을 쓴다(finding 코멘트 전용 경로가 없다).
// body에는 content만 담는다 — 서버가 비-PLAN 대상의 type/affectedFiles를 400으로 거부한다.
export const updateFindingComment = async (
  token: string,
  projectId: string,
  commentId: string,
  input: { content: string },
): Promise<FindingComment> => {
  const response = await fetchWithAuthRetry(buildRootCommentUrl(projectId, commentId), {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify({ content: input.content }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update finding comment: ${response.status}`);
  }

  return ((await response.json()) as FindingCommentResponse).data;
};

export const deleteFindingComment = async (token: string, projectId: string, commentId: string): Promise<void> => {
  const response = await fetchWithAuthRetry(buildRootCommentUrl(projectId, commentId), {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete finding comment: ${response.status}`);
  }
};
